/*

  BaseGame.ts

  Base abstract game class with common functionality

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { wordChecker } from "./wordcheck";
import { GlobalState } from "./globalstate";
import {
  BOARD_SIZE,
  IBaseGame, RACK_SIZE, ROWIDS,
  TileData, TileDict, TileScoreDict, TwoLetterLists,
} from "./types";
import { arrayEqual, coord, toVector } from "./util";
import { LocalStorage, getLocalStorage } from "./localstorage";

// Board type definitions
export type BoardType = "standard" | "explo";

export const START_SQUARE: Record<BoardType, string> = {
  standard: "H8",
  explo: "D4",
};

export const START_COORD: Record<BoardType, [number, number]> = {
  standard: [7, 7],
  explo: [3, 3],
};

// Board configurations
export const BOARD = {
  standard: {
    WORDSCORE: [
      "3      3      3",
      " 2           2 ",
      "  2         2  ",
      "   2       2   ",
      "    2     2    ",
      "               ",
      "               ",
      "3      2      3",
      "               ",
      "               ",
      "    2     2    ",
      "   2       2   ",
      "  2         2  ",
      " 2           2 ",
      "3      3      3"
    ],
    LETTERSCORE: [
      "   2       2   ",
      "     3   3     ",
      "      2 2      ",
      "2      2      2",
      "               ",
      " 3   3   3   3 ",
      "  2   2 2   2  ",
      "   2       2   ",
      "  2   2 2   2  ",
      " 3   3   3   3 ",
      "               ",
      "2      2      2",
      "      2 2      ",
      "     3   3     ",
      "   2       2   "
    ]
  },
  explo: {
    WORDSCORE: [
      "3      3      3",
      "        2      ",
      "         2     ",
      "   2           ",
      "    2          ",
      "     2      2  ",
      "      2      2 ",
      "3      2      3",
      " 2      2      ",
      "  2      2     ",
      "          2    ",
      "           2   ",
      "     2         ",
      "      2        ",
      "3      3      3"
    ],
    LETTERSCORE: [
      "    2      2   ",
      " 3   2       3 ",
      "  2   3     2  ",
      "       2  3   2",
      "2          3   ",
      " 2       2     ",
      "  3     2      ",
      "   2       2   ",
      "      2     3  ",
      "     2       2 ",
      "   3          2",
      "2   3  2       ",
      "  2     3   2  ",
      " 3       2   3 ",
      "   2      2    "
    ]
  }
};

export abstract class BaseGame implements IBaseGame {
  // Game identifier
  uuid: string;

  // Global state reference
  state: GlobalState;

  // Basic game properties that don't change while the game is underway
  locale = "is_IS";
  alphabet = "";
  tile_scores: TileScoreDict = {};
  board_type: BoardType = "standard";
  two_letter_words: TwoLetterLists = [[], []];
  startSquare = "H8";
  startCoord: [number, number] = [7, 7];
  manual: boolean = false; // Whether this is a manual word-check game

  // Game state
  tiles: TileDict = {};
  over: boolean = false;
  currentScore: number | undefined = undefined;
  currentWord: string = "";
  currentWordWithBlanks: string = "";
  currentCoord: string = "";
  wordGood: boolean = false;
  wordBad: boolean = false;
  currentError: string | number | null = null;
  currentMessage: string | null = null;
  localturn: boolean = true;
  moveInProgress: boolean = false;

  // UI state
  showingDialog: string | null = null;
  selectedSq: string | null = null;
  sel: string = "movelist";
  askingForBlank: { from: string; to: string; } | null = null;

  // Local storage
  localStorage: LocalStorage | null = null;

  constructor(uuid: string, state: GlobalState, board_type: BoardType = "standard") {
    this.uuid = uuid;
    this.localStorage = getLocalStorage(uuid);
    this.board_type = board_type;
    this.startSquare = START_SQUARE[board_type];
    this.startCoord = START_COORD[board_type];
    this.state = state;
  }

  // Abstract methods that must be implemented by subclasses
  abstract buttonState(): Record<string, boolean>;
  abstract allowDragDrop(): boolean;
  
  // Default init method that can be overridden
  init(): void {
    // Default implementation - can be overridden by subclasses
  }

  // Common methods
  tilescore(tile: string) {
    // Note: The Python naming convention of tile_scores is intentional
    return this.tile_scores[tile];
  };

  twoLetterWords() {
    // Note: The Python naming convention of two_letter_words is intentional
    return this.two_letter_words;
  };

  squareClass(coord: string): string | undefined {
    if (!coord || coord[0] === "R")
      return undefined;
    const {row, col} = toVector(coord);
    return this.squareType(row, col) || undefined;
  }

  squareType(row: number, col: number): string {
    const wsc = this.wordScore(row, col);
    if (wsc === 2)
      return "dw"; // Double word
    if (wsc === 3)
      return "tw"; // Triple word
    const lsc = this.letterScore(row, col);
    if (lsc === 2)
      return "dl"; // Double letter
    if (lsc === 3)
      return "tl"; // Triple letter
    return ""; // Plain square
  }

  wordScore(row: number, col: number): number {
    const wsc = BOARD[this.board_type].WORDSCORE;
    return parseInt(wsc[row].charAt(col)) || 1;
  }

  letterScore(row: number, col: number): number {
    const lsc = BOARD[this.board_type].LETTERSCORE;
    return parseInt(lsc[row].charAt(col)) || 1;
  }

  attemptMove(from: string, to: string) {
    if (to == from)
      // No move
      return;
    if (to in this.tiles && to[0] != 'R')
      throw "Square " + to + " occupied";
    if (!(from in this.tiles))
      throw "No tile at " + from;
    const tile = this.tiles[from];
    if (to[0] != 'R' && tile.tile == '?' && tile.letter == ' ') {
      // Dropping a blank tile on the board:
      // postpone the move and ask for its meaning
      this.askingForBlank = { from: from, to: to };
      return;
    }
    // Complete the move
    this.moveTile(from, to);
  };

  private _moveTile(from: string, to: string) {
    // Low-level function to move a tile between cells/slots
    if (from == to)
      // Nothing to do
      return;
    let fromTile = this.tiles[from];
    if (fromTile === undefined)
      throw "Moving from an empty square";
    delete this.tiles[from];
    if (to in this.tiles) {
      if (to.charAt(0) != "R")
        throw "Dropping to an occupied square";
      // Dropping to an occupied slot in the rack:
      // create space in the rack
      let dest = parseInt(to.slice(1));
      let empty = dest + 1;
      // Try to find an empty slot to the right of the drop destination
      while (('R' + empty) in this.tiles)
        empty++;
      if (empty <= RACK_SIZE) {
        // Found empty slot after the tile:
        // move the intervening tiles to the right
        for (let j = empty; j > dest; j--)
          this.tiles['R' + j] = this.tiles['R' + (j - 1)];
      }
      else {
        // No empty slots after the tile: try to find one to the left
        empty = dest - 1;
        while (('R' + empty) in this.tiles)
          empty--;
        if (empty < 1)
          throw "No place in rack to drop tile";
        for (let j = empty; j < dest; j++)
          this.tiles['R' + j] = this.tiles['R' + (j + 1)];
      }
    }
    if (to[0] == 'R' && fromTile.tile == '?')
    // Putting a blank tile back into the rack: erase its meaning
      fromTile.letter = ' ';
    this.tiles[to] = fromTile;
  };

  moveTile(from: string, to: string) {
    // High-level function to move a tile between cells/slots
    this._moveTile(from, to);
    // Clear error message, if any
    this.currentError = this.currentMessage = null;
    // Update the current word score
    this.updateScore();
    // Update the local storage
    this.saveTiles();
  };

  cancelBlankDialog() {
    // Cancel the dialog asking for the meaning of the blank tile
    this.askingForBlank = null;
  };

  placeBlank(letter: string) {
    // Assign a meaning to a blank tile that is being placed on the board
    if (this.askingForBlank === null)
      return;
    const { from, to } = this.askingForBlank;
    // We must assign the tile letter before moving it
    // since moveTile() calls updateScore() which in turn does a /wordcheck
    this.tiles[from].letter = letter;
    this.moveTile(from, to);
    this.askingForBlank = null;
  };

  tilesPlaced(): string[] {
    // Return a list of coordinates of tiles that the user has
    // placed on the board by dragging from the rack
    const r: string[] = [];
    for (let sq in this.tiles)
      if (this.tiles.hasOwnProperty(sq) &&
        sq[0] != 'R' && this.tiles[sq].draggable)
        // Found a non-rack tile that is not glued to the board
        r.push(sq);
    return r;
  };

  resetRack() {
    this.selectedSq = null;
    this._resetRack();
    this.saveTiles();
  }

  protected _resetRack() {
    // Recall all unglued tiles into the rack
    const t = this.tilesPlaced();
    if (t.length) {
      let i = 1;
      for (let j = 0; j < t.length; j++) {
        // Find a free slot in the rack
        while (("R" + i) in this.tiles)
          i++;
        const sq = "R" + i;
        // Recall the tile
        this.tiles[sq] = this.tiles[t[j]];
        delete this.tiles[t[j]];
        if (this.tiles[sq].tile === '?')
          // Erase the meaning of the blank tile
          this.tiles[sq].letter = ' ';
        i++;
      }
      this.updateScore();
    }
    // Reset current error message, if any
    this.currentError = null;
  }

  rescrambleRack() {
    // Reorder the rack randomly. Bound to the Backspace key.
    this.selectedSq = null; // Currently selected (blinking) square
    if (this.showingDialog !== null)
      // Already showing a bottom-of-page dialog
      return;
    this._resetRack();
    const array: (TileData | null)[] = [];
    for (let i = 1; i <= RACK_SIZE; i++) {
      const rackTileId = "R" + i;
      if (rackTileId in this.tiles)
        array.push(this.tiles[rackTileId]);
      else
        array.push(null);
    }
    let currentIndex = array.length;
    // Fisher-Yates (Knuth) shuffle algorithm
    while (0 !== currentIndex) {
      const randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;
      const temporaryValue = array[currentIndex];
      array[currentIndex] = array[randomIndex];
      array[randomIndex] = temporaryValue;
    }
    // Fill the resulting rack from left to right
    let empty = 0; // Destination rack cell
    for (let i = 1; i <= RACK_SIZE; i++) {
      const a = array[i-1];
      if (a !== null)
        // Nonempty result cell: copy it
        this.tiles["R" + (i - empty)] = a;
      else {
        // Empty result cell: empty a rack cell from the right-hand side
        delete this.tiles["R" + (RACK_SIZE - empty)];
        empty++;
      }
    }
    this.saveTiles();
  };

  resetError() {
    // Reset the current error message, if any
    this.currentError = this.currentMessage = null;
  }

  placeTiles(move?: number, noHighlight?: boolean) {
    // Base implementation - can be overridden by subclasses
    this.tiles = {};
    // Subclasses should implement their specific logic
  }

  async updateScore() {
    // Re-calculate the current word score
    const scoreResult = this.calcScore();
    this.wordGood = false;
    this.wordBad = false;
    if (scoreResult === undefined || !scoreResult.word) {
      this.currentScore = undefined;
      this.currentWord = "";
      this.currentWordWithBlanks = "";
      this.currentCoord = "";
    } else {
      this.currentScore = scoreResult.score;
      this.currentWord = scoreResult.word;
      this.currentWordWithBlanks = scoreResult.wordWithBlanks;
      this.currentCoord = scoreResult.coord;
      if (!this.manual) {
        // This is not a manual-wordcheck game:
        // Check the word that has been laid down
        const found = await wordChecker.checkWords(this.state, this.locale, scoreResult.words);
        this.wordGood = found;
        this.wordBad = !found;
      }
    }
  };

  calcScore() {
    // Calculate the score for the tiles that have been laid on the board in the current move
    let score = 0, crossScore = 0;
    let wsc = 1;
    let minrow = BOARD_SIZE, mincol = BOARD_SIZE;
    let maxrow = 0, maxcol = 0;
    let numtiles = 0, numcrosses = 0;
    let word = "";
    let wordWithBlanks = "";
    let words: string[] = [];
    // Find out how many tiles were on the board before this move
    const numPrevTiles = Object.values(this.tiles).filter(t => !t.draggable).length;
    this.tilesPlaced().forEach((sq) => {
      // Tile on the board
      const row = ROWIDS.indexOf(sq.charAt(0));
      const col = parseInt(sq.slice(1)) - 1;
      const t = this.tiles[sq];
      score += t.score * this.letterScore(row, col);
      numtiles++;
      wsc *= this.wordScore(row, col);
      if (row < minrow)
        minrow = row;
      if (col < mincol)
        mincol = col;
      if (row > maxrow)
        maxrow = row;
      if (col > maxcol)
        maxcol = col;
    });
    if (!numtiles)
      return undefined;
    if (minrow !== maxrow && mincol !== maxcol)
      return undefined;
    let x = mincol, y = minrow;
    let dx: -1 | 0 | 1 = 0, dy: -1 | 0 | 1 = 0;
    if (minrow !== maxrow)
      dy = 1;
    else
    if (mincol === maxcol &&
      (this.tileAt(minrow - 1, mincol) !== null || this.tileAt(minrow + 1, mincol) !== null)) {
      // Single tile: if it has tiles above or below, consider this a vertical move
      dy = 1;
    } else {
      dx = 1; // Horizontal move
    }
    // Find the beginning of the word
    while (this.tileAt(y - dy, x - dx) !== null) {
      x -= dx;
      y -= dy;
    }
    const startCoord = coord(y, x, dy === 1) || "";
    let t: TileData | null;
    // Find the end of the word
    while ((t = this.tileAt(y, x)) !== null) {
      if (t.draggable) {
        // Add score for cross words
        const csc = this.calcCrossScore(y, x, 1 - dy, 1 - dx);
        if (csc.score >= 0) {
          // There was a cross word there (it can score 0 if blank)
          crossScore += csc.score;
          numcrosses++;
          words.push(csc.word);
        }
      }
      else {
        // This is a tile that was previously on the board
        score += t.score;
        numcrosses++;
      }
      // Accumulate the word being formed
      word += t.letter;
      if (t.draggable && t.tile === '?') {
        // This is a blank tile placed by the player - mark it with ?
        wordWithBlanks += '?' + t.letter;
      } else {
        wordWithBlanks += t.letter;
      }
      x += dx;
      y += dy;
    }
    if (numPrevTiles === 0) {
      // First move that actually lays down tiles must go through start square
      const c = this.startCoord;
      if (null === this.tileAt(c[0], c[1]))
        // No tile in the start square
        return undefined;
    }
    else
    if (!numcrosses)
      // Not first move, and not linked with any word on the board
      return undefined;
    // Check whether word is consecutive
    // (which it is not if there is an empty square before the last tile)
    if (dx && (x <= maxcol))
      return undefined;
    if (dy && (y <= maxrow))
      return undefined;
    words.push(word);
    return {
      word,
      wordWithBlanks,
      words,
      score: score * wsc + crossScore + (numtiles === RACK_SIZE ? 50 : 0),
      coord: startCoord
    };
  }

  calcCrossScore(oy: number, ox: number, dy: number, dx: number) {
    // Calculate the score contribution of a cross word
    let score = 0;
    let hascross = false;
    let x = ox, y = oy;
    let word = "";
    // Find the beginning of the word
    while (this.tileAt(y - dy, x - dx) !== null) {
      x -= dx;
      y -= dy;
    }
    let t: TileData | null;
    while ((t = this.tileAt(y, x)) !== null) {
      let sc = t.score;
      if (x === ox && y === oy)
        sc *= this.letterScore(y, x);
      else
        hascross = true;
      word += t.letter;
      score += sc;
      x += dx;
      y += dy;
    }
    if (!hascross)
      return { score: -1, word: "" };
    return { score: score * this.wordScore(oy, ox), word: word };
  }

  tileAt(row: number, col: number): TileData | null {
    const c = coord(row, col);
    return c ? this.tiles[c] || null : null;
  }

  saveTiles() {
    // Save the current unglued tile configuration to local storage
    let tp: { sq: string; tile: string; }[] = [];
    const tilesPlaced = this.tilesPlaced();
    for (const sq of tilesPlaced) {
      const { tile, letter } = this.tiles[sq];
      // For blank tiles, store their meaning as well
      tp.push({sq, tile: tile === "?" ? tile + letter : tile});
    }
    // Also save tiles remaining in the rack
    for (let i = 1; i <= RACK_SIZE; i++) {
      const sq = `R${i}`;
      if (sq in this.tiles)
        tp.push({sq, tile: this.tiles[sq].tile});
    }
    this.localStorage?.saveTiles(tp);
  };

  restoreTiles(savedTiles: { sq: string; tile: string}[]) {
    // Restore the tile positions that were previously stored
    // in local storage
    if (!savedTiles.length)
      // Nothing to do
      return;
    let savedLetters: string[] = [];
    let rackLetters: string[] = [];
    let rackTiles: TileDict = {};
    // First, check that the saved tiles match the current rack
    for (let i = 0; i < savedTiles.length; i++)
      savedLetters.push(savedTiles[i].tile.charAt(0));
    for (let i = 1; i <= RACK_SIZE; i++)
      if (("R" + i) in this.tiles)
        rackLetters.push(this.tiles["R" + i].tile.charAt(0));
    savedLetters.sort();
    rackLetters.sort();
    if (!arrayEqual(savedLetters, rackLetters))
      // We don't have the same rack as when the state was saved:
      // give up
      return;
    // Save the original rack and delete the rack tiles
    // from the board
    for (let j = 1; j <= RACK_SIZE; j++)
      if (("R" + j) in this.tiles) {
        rackTiles["R" + j] = this.tiles["R" + j];
        delete this.tiles["R" + j];
      }
    // Attempt to move the saved tiles from the saved rack to
    // their saved positions. Note that there are several corner
    // cases, for instance multiple instances of the same letter tile,
    // that make this code less than straightforward.
    for (let i = 0; i < savedTiles.length; i++) {
      const saved_sq = savedTiles[i].sq;
      if (!(saved_sq in this.tiles)) {
        // The saved destination square is empty:
        // find the tile in the saved rack and move it there
        const tile = savedTiles[i].tile;
        for (let sq in rackTiles)
          if (rackTiles.hasOwnProperty(sq) &&
            rackTiles[sq].tile == tile.charAt(0)) {
            // Found the tile (or its equivalent) in the rack: move it
            if (tile.charAt(0) === "?")
              if (saved_sq.charAt(0) === "R")
                // Going to the rack: no associated letter
                rackTiles[sq].letter = " ";
              else
                // Going to a board square: associate the originally
                // chosen and saved letter
                rackTiles[sq].letter = tile.charAt(1);
            // ...and assign it
            this.tiles[saved_sq] = rackTiles[sq];
            delete rackTiles[sq];
            break;
          }
      }
    }
    // Allocate any remaining tiles to free slots in the rack
    let j = 1;
    for (let sq in rackTiles)
      if (rackTiles.hasOwnProperty(sq)) {
        // Look for a free slot in the rack
        while(("R" + j) in this.tiles)
          j++;
        if (j <= RACK_SIZE)
          // Should always be true unless something is very wrong
          this.tiles["R" + j] = rackTiles[sq];
      }
    // The local storage may have been cleared before calling
    // restoreTiles() so we must ensure that it is updated
    this.saveTiles();
    // Show an updated word status and score
    this.updateScore();
  };

  cleanup() {
    // Base cleanup - can be overridden by subclasses
  }
}
